/*
 * Live-Home announcement card. Frosted-glass surface (consumes the
 * shared .glass / .glass--solid utilities in app.css for the blur +
 * subtle white border + inset shadow), with a soft accent ring on
 * top so it reads as "the announcement on the home wall" rather than
 * a generic panel.
 *
 * Three pieces stacked vertically:
 *   - .headline   greeting (h2) + message-of-the-day (p)
 *   - .divider    hairline (only when both sections render)
 *   - .todays     "Today we have:" eyebrow + chip-list of scheduled
 *                 announcements that match today's weekday
 */

.card {
  margin: 0 auto;
  width: 100%;
  max-width: min(86vw, 1200px);
  padding: clamp(22px, 2.4vw, 36px) clamp(28px, 3vw, 52px) clamp(20px, 2vw, 30px);
  display: flex;
  flex-direction: column;
  gap: clamp(12px, 1.4vw, 22px);
  /* Subtle accent ring so the card stands out against the bg without
   * fighting the glass material. */
  outline: 1.5px solid color-mix(in srgb, var(--token-accent) 55%, transparent);
  outline-offset: -1.5px;
  font-family: var(--token-font-ui);
  color: var(--token-text-primary);
}

/* ---------- Headline (greeting + message) ---------- */

.headline {
  display: flex;
  flex-direction: column;
  gap: clamp(14px, 1.2vw, 22px);
  text-align: center;
}

.greeting {
  margin: 0;
  font-family: var(--token-font-display);
  font-size: clamp(48px, 5.3vw, 91px);
  line-height: 1.2;
  letter-spacing: 0.5px;
  color: var(--token-text-primary);
}

.message {
  margin: 0;
  font-family: var(--token-font-body);
  /* Bumped two scale steps from clamp(20px, 1.8vw, 30px) so the
   * teacher's message-of-the-day reads from the back of the room
   * for K-6 kids. Pairs with the roomier line-height below. */
  font-size: clamp(28px, 2.4vw, 44px);
  line-height: 1.7;
  white-space: pre-wrap;
  color: var(--token-text-primary);
}

.messagePlaceholder {
  font-style: italic;
  color: rgba(28, 38, 32, 0.5);
}

/* ---------- Divider between sections ---------- */

.divider {
  height: 1px;
  background: color-mix(in srgb, var(--token-accent) 32%, transparent);
  margin: 0;
  border: 0;
}

/* ---------- Today we have: ---------- */

.todays {
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: center;
}

.todaysHead {
  margin: 0;
  font-family: var(--token-font-ui);
  font-size: clamp(13px, 1.1vw, 16px);
  font-weight: 700;
  letter-spacing: 0.7px;
  text-transform: uppercase;
  color: rgba(28, 38, 32, 0.6);
}

.todaysList {
  list-style: none;
  margin: 0 auto;
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: clamp(10px, 0.9vw, 16px);
  width: 100%;
  max-width: min(680px, 100%);
}

.todaysItem {
  width: 100%;
  padding: clamp(10px, 1vw, 14px) 18px;
  border-radius: 12px;
  background: color-mix(in srgb, var(--token-accent) 18%, transparent);
  border: 1px solid color-mix(in srgb, var(--token-accent) 45%, transparent);
  color: #000;
  font-family: var(--token-font-ui);
  font-size: clamp(15px, 1.3vw, 20px);
  font-weight: 600;
  letter-spacing: 0.2px;
  line-height: 1.6;
  text-align: center;
}
